import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSignLanguageDetector } from '../hooks/useSignLanguageDetector';
import { useTrainingRecorder } from '../hooks/useTrainingRecorder';
import type { TrainingBundlePayload } from '../training/types';
import {
  framesHaveHandLandmarks,
  handFocusSupportsMirrorAugmentation,
  resolveHandFocus,
  simplifyHandFocus,
  suggestHandFocus,
  type SimplifiedHandFocus,
} from '../training/handUtils';
import { buildValidationSummary } from '../training/trainingBundle';
import {
  formatBytes,
  getDetectorStatusLabel,
  getDetectorStatusTone,
  getPhotoStatusPill,
  getRecordingStatusLabel,
  getRecordingStatusPill,
  getTrainingRecorderBannerMessage,
} from './trainingRecorderUtils';


const validationIssueLabels: Record<string, string> = {
  too_few_frames: 'Zu kurze Aufnahme',
  insufficient_motion: 'Zu wenig Bewegung erkannt',
  landmarks_missing: 'Hand-Landmarks fehlen teilweise',
  values_out_of_range: 'Hände waren zeitweise außerhalb des Bildes',
  hand_coverage_low: 'Hände nicht durchgängig im Bild',
  pose_coverage_low: 'Oberkörper nicht durchgängig im Bild',
  face_coverage_low: 'Gesicht nicht durchgängig im Bild',
  hand_jitter_high: 'Handbewegung zu unruhig',
  pose_jitter_high: 'Körperhaltung zu unruhig',
  face_jitter_high: 'Kopf/Gesicht zu unruhig',
};

function mirrorCoordinatePoint(point: number[]): number[] {
  if (!Array.isArray(point) || point.length === 0) return [];
  const [x = 0, y = 0, z = 0, ...rest] = point;
  return [1 - x, y, z, ...rest];
}

function mirrorHandednessLabel(label: string): string {
  if (label === 'Left') return 'Right';
  if (label === 'Right') return 'Left';
  return label;
}

function mirrorFramesForSelfiePreview(frames: TrainingBundlePayload['frames']): TrainingBundlePayload['frames'] {
  return frames.map((frame) => ({
    ...frame,
    landmarks: Array.isArray(frame.landmarks)
      ? frame.landmarks.map((hand) => (Array.isArray(hand) ? hand.map((point) => mirrorCoordinatePoint(point)) : []))
      : [],
    ...(Array.isArray(frame.poseLandmarks)
      ? { poseLandmarks: frame.poseLandmarks.map((point) => mirrorCoordinatePoint(point)) }
      : {}),
    ...(Array.isArray(frame.faceLandmarks)
      ? { faceLandmarks: frame.faceLandmarks.map((point) => mirrorCoordinatePoint(point)) }
      : {}),
    ...(Array.isArray(frame.handedness)
      ? { handedness: frame.handedness.map((label) => mirrorHandednessLabel(label)) }
      : {}),
  }));
}

async function createStillFileFromDataUrl(dataUrl: string, mirrored: boolean): Promise<File | null> {
  let fallbackFile: File | null = null;
  try {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const mimeType = blob.type || 'image/jpeg';
    fallbackFile = new File([blob], 'still.jpg', { type: mimeType });

    if (!mirrored) {
      return fallbackFile;
    }

    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      return fallbackFile;
    }

    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    context.setTransform(1, 0, 0, 1, 0, 0);

    const mirroredBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, 0.95));
    if (!mirroredBlob) {
      return fallbackFile;
    }

    return new File([mirroredBlob], 'still.jpg', { type: mirroredBlob.type || mimeType });
  } catch (error) {
    console.warn('Failed to convert still image to File', error);
    return fallbackFile;
  }
}

export interface TrainingRecorderProps {
  profileId: string;
  label: string;
  symbolId?: string;
  onRecordingComplete: (payload: TrainingBundlePayload) => void;
}

export function TrainingRecorder({ profileId, label, symbolId, onRecordingComplete }: TrainingRecorderProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const isMountedRef = useRef(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showRawVideo, setShowRawVideo] = useState(true);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingStartTimeRef = useRef<number | null>(null);
  const [manualStillFile, setManualStillFile] = useState<File | null>(null);
  const [needsStillConfirmation, setNeedsStillConfirmation] = useState(false);
  const [detectorStartFeedback, setDetectorStartFeedback] = useState('');
  const [photoMode, setPhotoMode] = useState<'idle' | 'previewing' | 'captured'>('idle');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>(() => {
    try {
      const persisted = window.localStorage.getItem('cameraFacingMode');
      return persisted === 'user' || persisted === 'environment' ? persisted : 'user';
    } catch {
      // localStorage might be disabled (e.g., in private browsing)
      return 'user';
    }
  });
  const isMirroredPreview = facingMode === 'user';
  const [showHandDecisionOptions, setShowHandDecisionOptions] = useState(false);
  const [handFocusSelection, setHandFocusSelection] = useState<SimplifiedHandFocus>('both_hands');
  const [handFocusSuggestion, setHandFocusSuggestion] = useState<ReturnType<typeof suggestHandFocus> | null>(null);
  const resolvedHandFocus = useMemo(
    () => resolveHandFocus(handFocusSelection, handFocusSuggestion),
    [handFocusSelection, handFocusSuggestion],
  );
  const mirrorAugmentationEnabled = handFocusSupportsMirrorAugmentation(resolvedHandFocus);
  const metadataReady = profileId.trim().length > 0 && label.trim().length > 0;
  const metadataError = metadataReady
    ? ''
    : 'Bitte wähle ein Profil und eine Gebärde aus, bevor du eine Aufnahme startest oder hochlädst.';

  const cameraSupported = useMemo(
    () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia),
    [],
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // No longer using window globals - configuration persisted via localStorage

  const { start: startCamera, stop: stopCamera, status, error: cameraError, lastLandmarks, getVariationMetrics } = useSignLanguageDetector(
    videoRef,
    overlayRef,
    { telemetrySource: 'training_recorder' },
  );

  const {
    state,
    recordedData,
    startRecording,
    stopRecording,
    resetRecording,
    framesCaptured,
    clipLimitExceeded,
    maxClipBytes,
    previewLandmarks,
    previewHandedness,
    previewPoseLandmarks,
    previewFaceLandmarks,
    lastFrameReceivedAt,
  } = useTrainingRecorder(videoRef);
  const recordingFacingMode = recordedData.capturedFacingMode ?? facingMode;
  const recordingPreviewMirrored = recordingFacingMode === 'user';
  const normalizedRecordedFrames = useMemo(
    () => (recordingPreviewMirrored ? mirrorFramesForSelfiePreview(recordedData.frames) : recordedData.frames),
    [recordingPreviewMirrored, recordedData.frames],
  );

  // Auto-start camera immediately when detector is idle/stopped.
  useEffect(() => {
    if (!cameraSupported) {
      return;
    }
    if (status === 'idle' || status === 'stopped') {
      void startCamera();
    }
  }, [cameraSupported, status, startCamera]);

  // Update recording duration
  useEffect(() => {
    if (state !== 'recording') {
      recordingStartTimeRef.current = null;
      setRecordingDuration(0);
      return;
    }

    recordingStartTimeRef.current = Date.now();
    const interval = setInterval(() => {
      if (recordingStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
        setRecordingDuration(elapsed);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [state]);

  const handleStartRecording = useCallback(async () => {
    if (!metadataReady) {
      setDetectorStartFeedback(metadataError);
      return;
    }
    setDetectorStartFeedback('');
    setHandFocusSuggestion(null);
    if (status !== 'running') {
      setDetectorStartFeedback('Detektor wird gestartet…');
      const started = await startCamera();
      if (!started) {
        setDetectorStartFeedback(
          cameraError ??
            'Kamera konnte nicht gestartet werden. Bitte erlaube den Zugriff und versuche es erneut.',
        );
        return;
      }
      setDetectorStartFeedback('Detektor gestartet. Aufnahme bereit.');
    }
    startRecording(facingMode);
  }, [cameraError, facingMode, metadataError, metadataReady, startCamera, startRecording, status]);

  const handleStopRecording = useCallback(() => {
    stopRecording();
    // Auto-suggest hand focus based on recorded motion after stopping
    if (recordedData.frames.length > 1) {
      const suggestion = suggestHandFocus(recordedData.frames);
      setHandFocusSuggestion(suggestion);
      // Only auto-apply if high confidence and different from current selection
      if (suggestion.confidence === 'high') {
        const nextSelection = simplifyHandFocus(suggestion.suggestion);
        if (nextSelection !== handFocusSelection) {
          setHandFocusSelection(nextSelection);
        }
      }
    } else {
      setHandFocusSuggestion(null);
    }
  }, [stopRecording, recordedData.frames, handFocusSelection]);

  const finalizeSaveRecording = useCallback(
    async () => {
      if (!metadataReady) {
        return;
      }
      if (recordedData.frames.length === 0) {
        return;
      }

      const hasHandLandmarks = framesHaveHandLandmarks(recordedData.frames);
      setNeedsStillConfirmation(false);
      if (!hasHandLandmarks) {
        setDetectorStartFeedback('Keine Hand-Landmarks erkannt. Bitte nimm die Gebärde erneut mit sichtbaren Händen auf.');
        return;
      }

      setDetectorStartFeedback('');

      // Convert still image to File if available
      let stillFile: File | null = null;
      if (manualStillFile) {
        stillFile = manualStillFile;
      } else if (recordedData.stillImage) {
        stillFile = await createStillFileFromDataUrl(recordedData.stillImage, recordingPreviewMirrored);
      }

      const clipBytes = recordedData.clipFile?.size ?? (recordedData.clipSizeBytes > 0 ? recordedData.clipSizeBytes : undefined);
      const clipMimeType = recordedData.clipFile?.type;
      const stillBytes = stillFile?.size;
      const stillMimeType = stillFile?.type;
      
      // Get variation learning insights
      const metrics = getVariationMetrics(label.trim());
      const variationData = metrics ? {
        ...(metrics.dominantCluster !== 'none' ? { clusterId: metrics.dominantCluster } : {}),
        ...(metrics.variationDiversity !== undefined ? { variationDiversity: metrics.variationDiversity } : {}),
        ...(metrics.activeClusters !== undefined ? { canonicalTemplates: metrics.activeClusters } : {}),
      } : null;

      const recording = {
        ...(recordedData.frameCount > 0 ? { frameCount: recordedData.frameCount } : {}),
        ...(recordedData.clipDurationMs > 0 ? { clipDurationMs: recordedData.clipDurationMs } : {}),
        ...(typeof clipBytes === 'number' ? { clipBytes } : {}),
        ...(clipMimeType ? { clipMimeType } : {}),
        ...(typeof stillBytes === 'number' ? { stillBytes } : {}),
        ...(stillMimeType ? { stillMimeType } : {}),
        previewMirrored: recordingPreviewMirrored,
      };

      const payload: TrainingBundlePayload = {
        profileId: profileId.trim(),
        label: label.trim(),
        ...(symbolId?.trim() ? { symbolId: symbolId.trim() } : {}),
        frames: normalizedRecordedFrames,
        capturedAt: new Date().toISOString(),
        source: 'web://mediapipe',
        stillFile,
        clipFile: recordedData.clipFile,
        handFocus: resolvedHandFocus,
        ...(variationData && Object.keys(variationData).length > 0 ? { variationData } : {}),
        ...(Object.keys(recording).length > 0 ? { recording } : {}),
      };

      onRecordingComplete(payload);
      resetRecording();
      setManualStillFile(null);
      setHandFocusSuggestion(null);
    },
    [
      metadataReady,
      recordedData,
      recordingPreviewMirrored,
      normalizedRecordedFrames,
      profileId,
      label,
      symbolId,
      onRecordingComplete,
      resetRecording,
      manualStillFile,
      resolvedHandFocus,
      getVariationMetrics,
    ],
  );

  const handleSaveRecording = useCallback(() => {
    if (!metadataReady || recordedData.frames.length === 0 || clipLimitExceeded) {
      return;
    }

    if (!manualStillFile && recordedData.stillImage) {
      setNeedsStillConfirmation(true);
      setDetectorStartFeedback('Bestätige, ob du das letzte Videoframe als Referenz nutzen möchtest.');
      return;
    }

    if (!manualStillFile && !recordedData.stillImage) {
      setNeedsStillConfirmation(false);
      setDetectorStartFeedback('Bitte wähle ein Referenzbild aus, bevor du die Aufnahme verwendest.');
      return;
    }

    void finalizeSaveRecording();
  }, [clipLimitExceeded, finalizeSaveRecording, manualStillFile, metadataReady, recordedData]);

  const handleConfirmAutoStill = useCallback(() => {
    void finalizeSaveRecording();
  }, [finalizeSaveRecording]);

  const handleCancelAutoStill = useCallback(() => {
    setNeedsStillConfirmation(false);
    setDetectorStartFeedback('');
  }, []);

  const handleSaveLandmarkJson = useCallback(() => {
    if (normalizedRecordedFrames.length === 0) {
      return;
    }
    const blob = new Blob([JSON.stringify({ frames: normalizedRecordedFrames }, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'training-landmarks.json';
    link.click();
    URL.revokeObjectURL(url);
  }, [normalizedRecordedFrames]);

  const handleDiscardRecording = useCallback(() => {
    resetRecording();
    setRecordingDuration(0);
    setManualStillFile(null);
    setNeedsStillConfirmation(false);
    setDetectorStartFeedback('');
    setHandFocusSuggestion(null);
  }, [resetRecording]);

  const handleManualStillChange = useCallback(
    (file: File | null) => {
      setManualStillFile(file);
      if (file) {
        setNeedsStillConfirmation(false);
        setDetectorStartFeedback('');
      }
    },
    [],
  );

  const handleStartPhotoPreview = useCallback(async () => {
    if (!cameraSupported) {
      setDetectorStartFeedback('Kamera wird nicht unterstützt. Bitte wähle stattdessen ein Bild aus.');
      return;
    }

    if (status !== 'running') {
      setDetectorStartFeedback('Kamera wird gestartet…');
      const started = await startCamera();
      if (!started) {
        setDetectorStartFeedback(
          cameraError ?? 'Kamera konnte nicht gestartet werden. Bitte erlaube den Zugriff und versuche es erneut.',
        );
        return;
      }
    }

    setPhotoMode('previewing');
    setDetectorStartFeedback('Vorschau aktiv. Positioniere dich und klicke "Foto aufnehmen".');
  }, [cameraError, cameraSupported, startCamera, status]);

  const handleCapturePhoto = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      setDetectorStartFeedback('Kamera noch nicht bereit. Bitte warte einen Moment.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      setDetectorStartFeedback('Bild konnte nicht aufgenommen werden. Bitte versuche es erneut.');
      return;
    }

    if (isMirroredPreview) {
      // Flip horizontally to match the mirrored selfie preview
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Reset transformation matrix
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.95));

    if (!blob) {
      setDetectorStartFeedback('Bild konnte nicht aufgenommen werden. Bitte versuche es erneut.');
      return;
    }

    const capturedFile = new File([blob], 'referenzbild.jpg', { type: blob.type || 'image/jpeg' });
    setManualStillFile(capturedFile);
    setPhotoMode('captured');
    setNeedsStillConfirmation(false);
    setDetectorStartFeedback('Foto aufgenommen. Bestätige oder nimm ein neues auf.');
  }, [isMirroredPreview]);

  const handleConfirmPhoto = useCallback(() => {
    setPhotoMode('idle');
    setDetectorStartFeedback('Foto als Referenzbild übernommen.');
  }, []);

  const handleCancelPhoto = useCallback(() => {
    setPhotoMode('idle');
    setManualStillFile(null);
    setDetectorStartFeedback('');
  }, []);

  const handleSwitchCamera = useCallback(async () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    
    // Persist to localStorage
    try {
      window.localStorage.setItem('cameraFacingMode', newFacingMode);
    } catch {
      // localStorage might be disabled
    }
    
    // Update component state, which will trigger an effect to update window globals
    setFacingMode(newFacingMode);
    
    // Stop and restart camera with new facing mode if it's currently running
    if (status === 'running') {
      setDetectorStartFeedback('Kamera wird gewechselt…');
      
      // Stop the current camera first
      await stopCamera();
      
      // Wait a bit for cleanup
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Start with new facing mode
      const started = await startCamera();
      if (started) {
        if (isMountedRef.current) {
          setDetectorStartFeedback(
            newFacingMode === 'user' 
              ? 'Frontkamera aktiviert' 
              : 'Rückkamera aktiviert'
          );
        }
      } else {
        if (isMountedRef.current) {
          setDetectorStartFeedback(
            cameraError ?? 'Kamera konnte nicht gewechselt werden. Bitte versuche es erneut.'
          );
        }
        // Revert facing mode if switch failed
        if (isMountedRef.current) {
          try {
            window.localStorage.setItem('cameraFacingMode', facingMode);
          } catch {
            // localStorage might be disabled
          }
          setFacingMode(facingMode);
        }
      }
    }
  }, [cameraError, facingMode, startCamera, stopCamera, status]);

  const [manualStillPreviewUrl, setManualStillPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!manualStillFile) {
      setManualStillPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(manualStillFile);
    setManualStillPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [manualStillFile]);

  const detectorRunning = status === 'running';
  const hasLiveFrames = detectorRunning && lastLandmarks.length > 0;
  const latestHandCount = useMemo(
    () => previewLandmarks.filter((hand) => Array.isArray(hand) && hand.length > 0).length,
    [previewLandmarks],
  );
  const latestPointCount = useMemo(
    () =>
      previewLandmarks.reduce((total, hand) => (Array.isArray(hand) ? total + hand.length : total), 0),
    [previewLandmarks],
  );
  const poseLandmarksAvailable = previewPoseLandmarks.length > 0;
  const faceLandmarksAvailable = previewFaceLandmarks.length > 0;
  const handLandmarksAvailable = latestHandCount > 0;
  const detectorInactiveNotice = status === 'error'
    ? 'Die Kameraerkennung konnte nicht gestartet werden. Prüfe den Kamerazugriff und versuche es erneut.'
    : !detectorRunning
    ? 'Die Kameraerkennung wird vorbereitet. Frames und Standbilder erscheinen gleich.'
    : !hasLiveFrames
    ? 'Es kommen noch keine Live-Frames an. Positioniere dich vor der Kamera oder warte einen Moment.'
    : '';
  const showModalityGuidance = detectorRunning && lastFrameReceivedAt !== null;
  const modalityNotices = showModalityGuidance
    ? [
        !handLandmarksAvailable
          ? 'Bitte halte beide Hände sichtbar im Kamerabild.'
          : null,
        !poseLandmarksAvailable
          ? 'Bitte halte deinen Oberkörper im Bild, damit Pose-Landmarks erkannt werden.'
          : null,
        !faceLandmarksAvailable
          ? 'Bitte halte dein Gesicht im Bild, damit Gesichts-Landmarks erkannt werden.'
          : null,
      ].filter((message): message is string => Boolean(message))
    : [];

  const isRecording = state === 'recording';
  const hasRecording = state === 'idle' && recordedData.frames.length > 0;
  // Derive pose/face availability from the recorded frames themselves so that
  // the validation summary remains stable after stopping or leaving frame.
  // The live preview flags (poseLandmarksAvailable/faceLandmarksAvailable) are
  // only used for live guidance, not for validating the stored recording.
  const validationSummary = useMemo(
    () => {
      const frames = recordedData.frames;
      const poseEnabled = frames.some((f) => Array.isArray(f.poseLandmarks) && f.poseLandmarks.length > 0);
      const faceEnabled = frames.some((f) => Array.isArray(f.faceLandmarks) && f.faceLandmarks.length > 0);
      return buildValidationSummary(frames, { poseEnabled, faceEnabled });
    },
    [recordedData.frames],
  );
  const clipStatus = recordedData.clipFile
    ? `${recordedData.clipFile.name} (${formatBytes(recordedData.clipFile.size)})`
    : `${formatBytes(recordedData.clipSizeBytes)} aufgenommen`;
  const clipLimitNotice = clipLimitExceeded
    ? `Maximale Dateigröße überschritten (${formatBytes(maxClipBytes)}). Bitte kürzer aufnehmen.`
    : `Video wird zusammen mit den Landmarks gespeichert (Limit ${formatBytes(maxClipBytes)}).`;
  const uploadDisabled = clipLimitExceeded || !metadataReady || recordedData.frames.length === 0;
  const uploadDisabledReason = !metadataReady
    ? 'Bitte wähle ein Profil und eine Gebärde aus, bevor du eine Aufnahme startest oder hochlädst.'
    : recordedData.frames.length === 0
    ? 'Upload gesperrt, da keine aufgenommenen Frames vorhanden sind.'
    : clipLimitExceeded
    ? 'Upload gesperrt, weil die maximale Dateigröße überschritten wurde.'
    : '';
  const displayedLabel = label.trim() || 'Keine Gebärdenauswahl vorhanden';
  const detectorStatusLabel = getDetectorStatusLabel(status);
  const detectorStatusTone = getDetectorStatusTone(status);
  const recordingStatusLabel = getRecordingStatusLabel({ isRecording, hasRecording });
  const recordingStatusPill = getRecordingStatusPill({ isRecording, hasRecording, recordingDuration });
  const photoStatusPill = getPhotoStatusPill(photoMode);
  const framesLine = framesCaptured > 0
    ? `${framesCaptured} Frames erfasst`
    : detectorRunning
    ? 'Noch keine verwertbaren Frames empfangen'
    : status === 'error'
    ? 'Kamera nicht verfügbar'
    : 'Kamera wird vorbereitet…';
  const bannerMessage = useMemo(
    () =>
      getTrainingRecorderBannerMessage({
        photoMode,
        isRecording,
        hasRecording,
        detectorStatus: status,
        detectorRunning,
      }),
    [photoMode, isRecording, hasRecording, detectorRunning, status],
  );

  return (
    <section className="training-recorder">
      <div className="gesture-screen training-recorder__screen">
        <div className="video-wrapper gesture-fullscreen">
          <video
            ref={videoRef}
            className={['video', isMirroredPreview && 'mirrored', !showRawVideo && 'video-hidden']
              .filter(Boolean)
              .join(' ')}
            data-testid="training-video"
            playsInline
            muted
            autoPlay
          />
          <canvas
            ref={overlayRef}
            className={`overlay${showOverlay ? '' : ' overlay-hidden'}`}
            data-testid="overlay-canvas"
            aria-hidden={!showOverlay}
          />
          <div
            className={['video-veil', !showRawVideo && 'video-veil-hidden'].filter(Boolean).join(' ')}
            aria-hidden="true"
          />

          <div className="gesture-screen__hud">
            <div className="gesture-screen__status">
              <div className="gesture-screen__status-pill" data-testid="status-chip" data-state={detectorStatusTone}>
                <span className="gesture-screen__status-dot" />
                <span>{detectorStatusLabel}</span>
              </div>
              {photoStatusPill && <span className="gesture-screen__pill">{photoStatusPill}</span>}
              {recordingStatusPill && <span className="gesture-screen__pill">{recordingStatusPill}</span>}
            </div>
            <div className="gesture-screen__status-meta">
              <p>
                Profil <strong>{profileId || '–'}</strong> · Gebärde <strong>{displayedLabel}</strong>
              </p>
              <p>{framesLine}</p>
            </div>
          </div>
        </div>

        <div className="gesture-screen__controls">
          <div className="gesture-screen__banner">
            <span>{bannerMessage}</span>
          </div>

          <div className="gesture-screen__actions training-recorder__actions">
            {photoMode === 'idle' && (
              <>
                {!isRecording && !hasRecording && (
                  <>
                    <button
                      className="gesture-screen__action gesture-screen__action--confirm"
                      onClick={handleStartRecording}
                      disabled={!metadataReady || status === 'initializing'}
                    >
                      Aufnahme starten
                    </button>
                    <button
                      className="gesture-screen__action gesture-screen__action--alt"
                      onClick={handleStartPhotoPreview}
                      disabled={!cameraSupported || !metadataReady}
                    >
                      Foto mit Kamera
                    </button>
                  </>
                )}
                {isRecording && (
                  <button className="gesture-screen__action gesture-screen__action--confirm" onClick={handleStopRecording}>
                    Aufnahme stoppen
                  </button>
                )}
                {hasRecording && (
                  <>
                    <button
                      className="gesture-screen__action gesture-screen__action--confirm"
                      onClick={handleSaveRecording}
                      disabled={uploadDisabled}
                    >
                      Aufnahme verwenden
                    </button>
                    <button className="gesture-screen__action gesture-screen__action--alt" onClick={handleDiscardRecording}>
                      Verwerfen
                    </button>
                  </>
                )}
              </>
            )}
            {photoMode === 'previewing' && (
              <>
                <button className="gesture-screen__action gesture-screen__action--confirm" onClick={handleCapturePhoto}>
                  Foto aufnehmen
                </button>
                <button className="gesture-screen__action gesture-screen__action--alt" onClick={handleCancelPhoto}>
                  Abbrechen
                </button>
              </>
            )}
            {photoMode === 'captured' && (
              <>
                <button className="gesture-screen__action gesture-screen__action--confirm" onClick={handleConfirmPhoto}>
                  Foto verwenden
                </button>
                <button className="gesture-screen__action gesture-screen__action--learn" onClick={handleCapturePhoto}>
                  Erneut aufnehmen
                </button>
                <button className="gesture-screen__action gesture-screen__action--alt" onClick={handleCancelPhoto}>
                  Verwerfen
                </button>
              </>
            )}
          </div>

          <div className="gesture-screen__meta">
            <div className="gesture-screen__meta-actions">
              <button
                className="ghost-inline"
                onClick={handleSwitchCamera}
                disabled={!cameraSupported}
                title={facingMode === 'user' ? 'Zur Rückkamera wechseln' : 'Zur Frontkamera wechseln'}
              >
                {facingMode === 'user' ? '🔄 Rückkamera' : '🔄 Frontkamera'}
              </button>
              <label className="toggle ghost-inline" htmlFor="overlay-toggle">
                <input
                  id="overlay-toggle"
                  type="checkbox"
                  checked={showOverlay}
                  onChange={(event) => setShowOverlay(event.target.checked)}
                />
                <span>Overlay anzeigen</span>
              </label>
              <label className="toggle ghost-inline" htmlFor="raw-video-toggle">
                <input
                  id="raw-video-toggle"
                  type="checkbox"
                  checked={showRawVideo}
                  onChange={(event) => setShowRawVideo(event.target.checked)}
                />
                <span>Rohvideo anzeigen</span>
              </label>
            </div>

            <div className="notice-grid training-recorder__notice-grid" aria-live="polite" role="status">
              {!cameraSupported && (
                <div className="notice warning compact">
                  <strong>Kamera nicht verfügbar.</strong> Bitte erlaube den Kamerazugriff oder nutze ein Gerät mit Webcam.
                </div>
              )}

              {cameraError && <div className="notice error compact">{cameraError}</div>}

              {detectorInactiveNotice && (
                <div className={`notice ${detectorRunning ? 'info' : 'warning'} compact`}>
                  <strong>{detectorRunning ? 'Detektor aktiv, noch keine Erkennung' : 'Detektor pausiert.'}</strong>{' '}
                  {detectorInactiveNotice}
                </div>
              )}

              {detectorStartFeedback && (
                <div className={`notice ${detectorRunning ? 'info' : 'warning'} compact`}>{detectorStartFeedback}</div>
              )}

              {modalityNotices.length > 0 && (
                <div className="notice warning compact">
                  <strong>Hinweis zur Erkennung:</strong>
                  <ul>
                    {modalityNotices.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {hasRecording && validationSummary && (
                <div className={`notice ${validationSummary.issues.length === 0 ? 'info' : 'warning'} compact`}>
                  <strong>Qualitätscheck:</strong> {validationSummary.qualityScore}/100
                  {(validationSummary.issues.length > 0 || validationSummary.suggestions.length > 0) && (
                    <ul>
                      {validationSummary.issues.map((issue) => (
                        <li key={`issue-${issue}`}>Problem: {validationIssueLabels[issue] ?? issue}</li>
                      ))}
                      {validationSummary.suggestions.map((suggestion) => (
                        <li key={`suggestion-${suggestion}`}>Tipp: {suggestion}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {needsStillConfirmation && (
                <div className="notice info compact">
                  <p className="no-margin">
                    Kein Referenzbild ausgewählt. Möchtest du das letzte Videoframe als Referenz nutzen?
                  </p>
                  <div className="training-recorder__notice-actions">
                    <button className="primary" type="button" onClick={handleConfirmAutoStill}>
                      Ja, Frame verwenden
                    </button>
                    <button className="ghost" type="button" onClick={handleCancelAutoStill}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="panel training-recorder__details">
        <div className="panel-row">
          <div>
            <p className="eyebrow">Aufnahmedetails</p>
            <p className="muted small">Clip: {clipStatus}</p>
            {recordedData.clipDurationMs > 0 && (
              <p className="muted small">Dauer: {(recordedData.clipDurationMs / 1000).toFixed(1)}s</p>
            )}
            {recordedData.clipError && <div className="notice error">{recordedData.clipError}</div>}
            {!metadataReady && <div className="notice error">{metadataError}</div>}
            <div className={`notice ${clipLimitExceeded ? 'warning' : 'info'} spaced`}>
              {clipLimitNotice}
            </div>
          </div>
        </div>

        <div className="controls">
          {hasRecording && (
            <>
              <button className="ghost" onClick={handleSaveLandmarkJson}>
                Landmarks speichern
              </button>
              {uploadDisabledReason && <p className="muted small">{uploadDisabledReason}</p>}
            </>
          )}
        </div>

        <details className="training-recorder__technical">
          <summary>Technische Details anzeigen</summary>
          <div className="notice info spaced">
            <strong>Landmark-Stream</strong>{' '}
            {latestHandCount > 0
              ? `${latestHandCount} Hand${latestHandCount > 1 ? 'e' : ''} · ${latestPointCount} Punkte`
              : 'Noch keine Landmarken empfangen'}
            {lastFrameReceivedAt && (
              <>
                {' '}
                · Letzter Batch um {new Date(lastFrameReceivedAt).toLocaleTimeString()}
              </>
            )}
            {previewHandedness.length > 0 && (
              <p className="muted small">
                Händigkeit:{' '}
                {previewHandedness
                  .map((value) => (value === 'Left' ? 'links' : value === 'Right' ? 'rechts' : value))
                  .join(', ')}
              </p>
            )}
          </div>
          <p className="muted small">Detektorstatus: {detectorStatusLabel} · {recordingStatusLabel}</p>
        </details>

        <div className="form-group mt-sm">
          <button
            className="ghost"
            type="button"
            onClick={() => setShowHandDecisionOptions((current) => !current)}
            aria-expanded={showHandDecisionOptions}
          >
            {showHandDecisionOptions ? 'Handoptionen ausblenden' : 'Handoptionen anzeigen (optional)'}
          </button>
          <p className="muted small mt-xs">
            Standardmäßig werden beide Hände gemeinsam trainiert. Passe die Handoptionen nur an, wenn es für diese Gebärde nötig ist.
          </p>
          {showHandDecisionOptions && (
            <fieldset className="form-group mt-sm">
              <legend>Relevante Hand für diese Gebärde</legend>
              <div className="radio-group" role="radiogroup">
                <label className={`radio-label${handFocusSelection === 'both_hands' ? ' selected' : ''}`}>
                  <input
                    type="radio"
                    name="hand-focus"
                    value="both_hands"
                    checked={handFocusSelection === 'both_hands'}
                    onChange={() => setHandFocusSelection('both_hands')}
                  />
                  <span>Beide Hände zusammen</span>
                </label>
                <label className={`radio-label${handFocusSelection === 'dominant_only' ? ' selected' : ''}`}>
                  <input
                    type="radio"
                    name="hand-focus"
                    value="dominant_only"
                    checked={handFocusSelection === 'dominant_only'}
                    onChange={() => setHandFocusSelection('dominant_only')}
                  />
                  <span>Nur Haupthand</span>
                </label>
                <label className={`radio-label${handFocusSelection === 'either_hand' ? ' selected' : ''}`}>
                  <input
                    type="radio"
                    name="hand-focus"
                    value="either_hand"
                    checked={handFocusSelection === 'either_hand'}
                    onChange={() => setHandFocusSelection('either_hand')}
                  />
                  <span>Egal links oder rechts</span>
                </label>
              </div>
              {handFocusSuggestion && (
                <div className={`notice ${handFocusSuggestion.confidence === 'high' ? 'info' : 'warning'} compact mt-sm`}>
                  <strong>Automatische Erkennung:</strong> {handFocusSuggestion.reason}
                </div>
              )}
              <p className="muted small">
                Wähle nur noch grob aus, ob eine Hand führt, beide Hände zusammengehören oder die Gebärde links wie rechts klappt.
                Das Training erkennt bei zwei Händen automatisch, ob beide gleich wichtig sind oder unterschiedliche Rollen haben.
              </p>
              <p className="muted small">
                {mirrorAugmentationEnabled
                  ? 'Links/Rechts-Varianten werden für das Training automatisch gespiegelt, damit wenige Aufnahmen besser genutzt werden.'
                  : 'Für diese Gebärde bleibt das Training ungespiegelt, damit unterschiedliche Handrollen erhalten bleiben.'}
              </p>
            </fieldset>
          )}
        </div>

        <div className="form-group mt-sm">
          <label htmlFor="manual-still">Eigenes Referenzbild hochladen (optional)</label>
          <div className="file-input">
            <input
              id="manual-still"
              type="file"
              accept="image/*"
              onChange={(event) => handleManualStillChange(event.target.files?.[0] ?? null)}
            />
            <p className="muted small">
              {manualStillFile?.name || 'Wähle ein Bild aus deinen Dateien aus.'}
            </p>
          </div>
          <p className="muted small">
            Nutze "Foto mit Kamera" im Videobereich oder lade eine Datei hoch. Falls nichts ausgewählt ist, wirst du beim Abschluss gefragt, ob du das letzte Videoframe nutzen möchtest.
          </p>
        </div>

        {(recordedData.stillImage || manualStillPreviewUrl) && (
          <div className="still-preview">
            <p className="eyebrow">Vorschau</p>
            <img
              src={manualStillPreviewUrl ?? recordedData.stillImage ?? undefined}
              alt={manualStillPreviewUrl ? 'Hochgeladenes Referenzbild' : 'Aufgenommene Gebärde'}
              className={!manualStillPreviewUrl && recordingPreviewMirrored ? 'mirrored' : undefined}
            />
          </div>
        )}
      </div>
    </section>
  );
}
